<!-- eslint-disable -->
<template>
  <v-card elevation="2" class="post-card mb-4">
    <!-- Edit Mode -->
    <div v-if="isEditing" class="pa-4">
      <v-textarea
        v-model="editContent"
        variant="outlined"
        rows="4"
        counter="5000"
        autofocus
      ></v-textarea>
      <div class="d-flex" style="gap: 0.5rem;">
        <v-btn color="primary" @click="saveEdit" :loading="saving">{{ t('common.save') }}</v-btn>
        <v-btn variant="outlined" @click="cancelEdit">{{ t('common.cancel') }}</v-btn>
      </div>
    </div>

    <!-- Normal Display Mode -->
    <template v-else>
      <v-card-title class="d-flex align-center">
        <v-avatar color="primary" size="40" class="mr-3">
          <v-img v-if="post.avatarUrl" :src="post.avatarUrl"></v-img>
          <span v-else>{{ post.displayName?.charAt(0) || post.username?.charAt(0) || 'U' }}</span>
        </v-avatar>
        <div class="flex-grow-1">
          <div class="text-subtitle-1 font-weight-bold">
            {{ post.displayName || post.username }}
            <router-link
              v-if="post.groupId && post.groupName"
              :to="`/groups/${post.groupId}`"
              class="group-badge ml-2"
              @click.stop
            >
              <v-icon size="small">mdi-account-group</v-icon>
              {{ post.groupName }}
            </router-link>
          </div>
          <div class="text-caption text-grey">
            {{ formatDate(post.created_at) }} • {{ post.visibility || 'public' }}
          </div>
        </div>
        <v-menu v-if="canEdit">
          <template v-slot:activator="{ props: menuProps }">
            <v-btn icon="mdi-dots-vertical" v-bind="menuProps" size="small" variant="text"></v-btn>
          </template>
          <v-list>
            <v-list-item @click="startEdit">
              <template v-slot:prepend>
                <v-icon>mdi-pencil</v-icon>
              </template>
              <v-list-item-title>{{ t('common.edit') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="deletePost">
              <template v-slot:prepend>
                <v-icon color="error">mdi-delete</v-icon>
              </template>
              <v-list-item-title class="text-error">{{ t('common.delete') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-card-title>

      <v-card-text class="py-4">
        <!-- Poll (if any) -->
        <poll-widget
          v-if="poll"
          :poll="poll"
          :post-id="post.postId"
          class="mb-4"
          @voted="handleVoted"
        />

        <div class="post-content" ref="postContentRef" v-html="formatContent(translatedPostContent || post.content)"></div>

        <!-- Image Gallery (supports single and multiple images) -->
        <image-gallery
          v-if="hasImages"
          :images="postImages"
          :image-url="post.imageUrl"
          class="mt-4"
        />

        <div class="d-flex align-center mt-2" style="gap: 0.5rem;">
          <!-- Read Aloud Button for Post -->
          <v-btn
            v-if="ttsEnabled"
            variant="outlined"
            size="small"
            color="primary"
            @click="speakPost"
          >
            <v-icon start size="small">mdi-text-to-speech</v-icon>
            {{ $t('accessibility.readAloud') }}
          </v-btn>

          <!-- Translate Button for Post -->
          <v-btn
            v-if="translateTo && !translatedPostContent"
            variant="outlined"
            size="small"
            color="primary"
            @click="translatePost"
            :loading="translatingPost"
          >
            <v-icon start size="small">mdi-translate</v-icon>
            {{ $t('accessibility.translate') }}
          </v-btn>

          <!-- Show Original Button -->
          <v-btn
            v-if="translatedPostContent"
            variant="outlined"
            size="small"
            color="warning"
            @click="translatedPostContent = null"
          >
            <v-icon start size="small">mdi-translate-off</v-icon>
            {{ $t('accessibility.showOriginal') }}
          </v-btn>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Reactions -->
      <v-card-actions class="px-4 py-2">
        <v-menu>
          <template v-slot:activator="{ props: menuProps }">
            <v-btn
              icon
              size="small"
              v-bind="menuProps"
              variant="text"
              :color="userReaction ? 'primary' : ''"
            >
              <v-icon>{{ userReaction || 'mdi-emoticon-outline' }}</v-icon>
            </v-btn>
          </template>
          <v-card>
            <div class="d-flex pa-2" style="gap: 0.25rem;">
              <v-btn
                v-for="emoji in reactionEmojis"
                :key="emoji.icon"
                @click="toggleReaction(emoji.icon)"
                icon
                size="small"
                variant="text"
              >
                <v-icon>{{ emoji.icon }}</v-icon>
              </v-btn>
            </div>
          </v-card>
        </v-menu>
        <v-menu open-on-hover :open-delay="300" location="top" :disabled="reactionsCount === 0">
          <template #activator="{ props: tipProps }">
            <span v-bind="tipProps" class="text-caption ml-1 reactions-count">{{ reactionsCount }}</span>
          </template>
          <v-card class="reactors-card pa-2" min-width="160" max-width="240">
            <div
              v-for="r in reactions"
              :key="r.reactionId || r.userId"
              class="reactor-item d-flex align-center ga-2 py-1"
            >
              <v-icon size="16">{{ r.reactionIcon }}</v-icon>
              <span class="text-body-2">{{ r.displayName || r.username }}</span>
            </div>
          </v-card>
        </v-menu>

        <v-btn
          icon
          size="small"
          @click="toggleComments"
          class="ml-4"
          variant="text"
        >
          <v-icon>mdi-comment-outline</v-icon>
        </v-btn>
        <span class="text-caption ml-1">{{ post.commentsCount || 0 }}</span>
      </v-card-actions>

      <!-- Comments Section -->
      <v-expand-transition>
        <div v-show="showComments">
          <v-divider></v-divider>

          <!-- Comment Input -->
          <v-card-text class="pb-2">
            <!-- Non-member notice -->
            <v-alert
              v-if="readOnly"
              type="info"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              <v-icon start size="small">mdi-account-plus</v-icon>
              {{ t('groups.joinToComment') || 'Bliv medlem for at kommentere' }}
            </v-alert>
            <div v-else class="d-flex align-start mb-4" style="gap: 0.5rem;">
              <v-avatar color="primary" size="32">
                <span>{{ currentUserInitial }}</span>
              </v-avatar>
              <div class="flex-grow-1">
                <v-textarea
                  v-model="newComment"
                  :placeholder="quotedComment ? t('common.replyToComment') : t('common.writeComment')"
                  variant="outlined"
                  rows="2"
                  dense
                  hide-details
                  @keydown="handleCommentKeydown"
                ></v-textarea>

                <!-- Quoted comment preview -->
                <v-chip
                  v-if="quotedComment"
                  class="mt-2"
                  closable
                  @click:close="quotedComment = null"
                  size="small"
                >
                  <v-icon start size="small">mdi-format-quote-close</v-icon>
                  {{ quotedComment.content.substring(0, 50) }}...
                </v-chip>

                <div class="d-flex mt-2" style="gap: 0.5rem;">
                  <v-btn
                    size="small"
                    color="primary"
                    @click="postComment"
                    :loading="postingComment"
                    :disabled="!newComment || !newComment.trim()"
                  >
                    {{ t('common.post') }}
                  </v-btn>
                  <v-btn
                    v-if="quotedComment"
                    size="small"
                    variant="text"
                    @click="quotedComment = null"
                  >
                    {{ t('common.cancel') }}
                  </v-btn>
                </div>
              </div>
            </div>

            <!-- Loading comments -->
            <v-progress-circular
              v-if="loadingComments"
              indeterminate
              size="24"
              class="d-block mx-auto my-4"
            ></v-progress-circular>

            <!-- Comments List -->
            <div v-else-if="comments.length > 0" class="comments-list">
              <div
                v-for="comment in comments"
                :key="comment.commentId"
                class="comment-item d-flex mb-3"
                style="gap: 0.5rem;"
              >
                <v-avatar color="primary" size="32">
                  <span>{{ comment.displayName?.charAt(0) || comment.username?.charAt(0) || 'U' }}</span>
                </v-avatar>
                <div class="flex-grow-1">
                  <div class="comment-bubble pa-3 rounded">
                    <div class="font-weight-bold text-subtitle-2">
                      {{ comment.displayName || comment.username }}
                    </div>

                    <!-- Quoted comment -->
                    <div v-if="comment.quotedCommentId" class="quoted-comment my-2 pa-2">
                      <v-icon size="small">mdi-format-quote-close</v-icon>
                      {{ getQuotedCommentText(comment.quotedCommentId) }}
                    </div>

                    <div class="comment-text" v-html="formatContent(comment.translatedContent || comment.content)"></div>
                  </div>
                  <div class="d-flex align-center mt-1 text-caption text-grey" style="gap: 0.75rem;">
                    <span>{{ formatDate(comment.created_at) }}</span>

                    <!-- Read Aloud Button for Comment -->
                    <v-btn
                      v-if="ttsEnabled"
                      size="x-small"
                      variant="outlined"
                      color="primary"
                      @click="speakComment(comment)"
                    >
                      <v-icon size="small">mdi-text-to-speech</v-icon>
                    </v-btn>

                    <!-- Translate Button for Comment -->
                    <v-btn
                      v-if="translateTo && !comment.translatedContent"
                      size="x-small"
                      variant="outlined"
                      color="primary"
                      @click="translateComment(comment)"
                      :loading="comment.translating"
                    >
                      <v-icon size="small">mdi-translate</v-icon>
                    </v-btn>

                    <!-- Show Original for Comment -->
                    <v-btn
                      v-if="comment.translatedContent"
                      size="x-small"
                      variant="outlined"
                      color="warning"
                      @click="comment.translatedContent = null"
                    >
                      <v-icon size="small">mdi-translate-off</v-icon>
                    </v-btn>

                    <!-- Comment Like Button -->
                    <v-btn
                      size="x-small"
                      variant="text"
                      :color="comment.userReaction ? 'error' : 'grey'"
                      @click="toggleCommentLike(comment)"
                      :loading="comment.likeLoading"
                    >
                      <v-icon size="small" start>{{ comment.userReaction ? 'mdi-heart' : 'mdi-heart-outline' }}</v-icon>
                      {{ comment.reactionCount || 0 }}
                    </v-btn>

                    <v-btn
                      size="x-small"
                      variant="text"
                      @click="quoteComment(comment)"
                    >
                      <v-icon size="small" start>mdi-reply</v-icon>
                      {{ t('common.quote') }}
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-grey py-4">
              {{ t('common.noComments') }}
            </div>
          </v-card-text>
        </div>
      </v-expand-transition>
    </template>
  </v-card>

  <!-- Confirmation Dialog -->
  <confirm-dialog
    ref="confirmDialog"
    :title="t('common.delete')"
    :message="t('common.deleteConfirm')"
    :confirmText="t('common.delete')"
    :cancelText="t('common.cancel')"
    confirmColor="error"
    icon="mdi-delete-alert"
    iconColor="error"
  />
</template>

<script setup>
/* eslint-disable */
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import api from '../services/api'
import ConfirmDialog from './ConfirmDialog.vue'
import ImageGallery from './ImageGallery.vue'
import PollWidget from './PollWidget.vue'
import { accessibilitySettings, speak } from '../services/accessibility'
import { applyInlineFormatting } from '../utils/formatText'

const { t } = useI18n()

const props = defineProps({
  post: {
    type: Object,
    required: true
  },
  readOnly: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['deleted', 'updated'])

// Poll
const poll = ref(null)

const loadPoll = async () => {
  try {
    const res = await api.get(`/posts/${props.post.postId}/polls`)
    poll.value = res.data.poll || null
  } catch (e) {
    console.error('Failed to load poll', e)
  }
}

const handleVoted = () => loadPoll()

// Image gallery computed properties
const postImages = computed(() => {
  if (props.post.images) {
    if (typeof props.post.images === 'string') {
      try {
        return JSON.parse(props.post.images)
      } catch {
        return []
      }
    }
    return props.post.images
  }
  return []
})

const hasImages = computed(() => {
  return postImages.value.length > 0 || props.post.imageUrl
})

// Text-to-speech
const ttsEnabled = computed(() => accessibilitySettings.textToSpeechEnabled)
const postContentRef = ref(null)

const speakPost = () => {
  const authorName = props.post.displayName || props.post.username
  const content = translatedPostContent.value || props.post.content
  speak(`${authorName} skrev: ${content}`)
}

const speakComment = (comment) => {
  const authorName = comment.displayName || comment.username
  const content = comment.translatedContent || comment.content
  speak(`${authorName} kommenterede: ${content}`)
}

// Translation
const translateTo = computed(() => accessibilitySettings.translateTo)
const translatedPostContent = ref(null)
const translatingPost = ref(false)

const translateText = async (text, targetLang) => {
  try {
    const response = await api.post('/translate', { text, targetLang })
    return response.data.translatedText || text
  } catch (error) {
    console.error('Translation error:', error)
    return text
  }
}

const translatePost = async () => {
  if (!translateTo.value) return
  translatingPost.value = true
  try {
    translatedPostContent.value = await translateText(props.post.content, translateTo.value)
  } catch (error) {
    console.error('Failed to translate post:', error)
  } finally {
    translatingPost.value = false
  }
}

const translateComment = async (comment) => {
  if (!translateTo.value) return
  comment.translating = true
  try {
    comment.translatedContent = await translateText(comment.content, translateTo.value)
  } catch (error) {
    console.error('Failed to translate comment:', error)
  } finally {
    comment.translating = false
  }
}

const store = useStore()

// Refs
const confirmDialog = ref(null)

// Edit state
const isEditing = ref(false)
const editContent = ref('')
const saving = ref(false)

// Comments state
const showComments = ref(false)
const comments = ref([])
const loadingComments = ref(false)
const newComment = ref('')
const quotedComment = ref(null)
const postingComment = ref(false)

// Reactions state
const userReaction = ref(null)
const reactions = ref([])

const reactionEmojis = [
  { icon: 'mdi-heart', name: 'love' },
  { icon: 'mdi-thumb-up', name: 'like' },
  { icon: 'mdi-emoticon-happy', name: 'happy' },
  { icon: 'mdi-emoticon-excited', name: 'excited' },
  { icon: 'mdi-emoticon-sad', name: 'sad' },
  { icon: 'mdi-emoticon-angry', name: 'angry' }
]

const currentUser = computed(() => store.getters['auth/user'])
const canEdit = computed(() => {
  if (!currentUser.value || !props.post) return false
  return currentUser.value.userId === props.post.userId
})
const currentUserInitial = computed(() => currentUser.value?.displayName?.charAt(0) || 'U')

const reactionsCount = computed(() => reactions.value.length)

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 60) return `${diffMins} min`
  if (diffHours < 24) return `${diffHours} t`
  if (diffDays < 7) return `${diffDays} d`

  return date.toLocaleDateString('da-DK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Emoji mappings
const emojiMap = {
  ':-)': '😊', ':)': '😊', ':-D': '😀', ':D': '😀',
  ':-P': '😛', ':P': '😛', ':-p': '😛', ':p': '😛',
  ';-)': '😉', ';)': '😉', ':-O': '😮', ':O': '😮',
  ':-o': '😮', ':o': '😮', ':-|': '😐', ':|': '😐',
  ':-(': '😞', ':(': '😞', ":'(": '😢', ":\\'(": '😢',
  ':-*': '😘', ':*': '😘', '<3': '❤️', '</3': '💔',
  ':fire:': '🔥', ':+1:': '👍', ':-1:': '👎',
  ':thumbsup:': '👍', ':thumbsdown:': '👎', ':clap:': '👏',
  ':wave:': '👋', ':ok:': '👌', ':pray:': '🙏',
  ':100:': '💯', ':party:': '🎉', ':star:': '⭐',
  ':sun:': '☀️', ':moon:': '🌙', ':coffee:': '☕',
  ':beer:': '🍺', ':pizza:': '🍕', ':cake:': '🎂',
  'XD': '😆', 'xD': '😆', ':lol:': '😂', 'LOL': '😂',
  ':rofl:': '🤣', 'ROFL': '🤣', ':thinking:': '🤔',
  ':hmmm:': '🤔', ':cool:': '😎', ':angry:': '😠',
  ':poop:': '💩', ':skull:': '💀', ':ghost:': '👻',
  ':alien:': '👽', ':robot:': '🤖', ':heart_eyes:': '😍',
  ':money:': '💰', ':muscle:': '💪', ':eyes:': '👀',
  ':see_no_evil:': '🙈', ':hear_no_evil:': '🙉', ':speak_no_evil:': '🙊'
}

const formatContent = (content) => {
  if (!content) return ''

  let formatted = content

  formatted = formatted
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[^\s]*)?/gi
  formatted = formatted.replace(youtubeRegex, (match, videoId) => {
    return `<div class="video-embed"><iframe src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"></iframe></div>`
  })

  const vimeoRegex = /(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)(?:[^\s]*)?/gi
  formatted = formatted.replace(vimeoRegex, (match, videoId) => {
    return `<div class="video-embed"><iframe src="https://player.vimeo.com/video/${videoId}" frameborder="0" allowfullscreen allow="autoplay; fullscreen; picture-in-picture"></iframe></div>`
  })

  const urlRegex = /(https?:\/\/[^\s<]+[^\s<.,;:!?\-\])'"»])/gi
  formatted = formatted.replace(urlRegex, (match) => {
    if (match.includes('youtube.com/embed') || match.includes('player.vimeo.com')) {
      return match
    }
    return `<a href="${match}" target="_blank" rel="noopener noreferrer" class="post-link">${match}</a>`
  })

  formatted = formatted.replace(/@(\w+)/g, '<span class="mention">@$1</span>')

  const sortedEmojis = Object.entries(emojiMap).sort((a, b) => b[0].length - a[0].length)
  for (const [text, emoji] of sortedEmojis) {
    const escaped = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    formatted = formatted.replace(new RegExp(escaped, 'g'), emoji)
  }

  formatted = applyInlineFormatting(formatted)
  formatted = formatted.replace(/\n/g, '<br>')

  return formatted
}

// Edit functions
const startEdit = () => {
  editContent.value = props.post.content
  isEditing.value = true
}

const cancelEdit = () => {
  isEditing.value = false
  editContent.value = ''
}

const saveEdit = async () => {
  if (!editContent.value || !editContent.value.trim()) {
    alert(t('common.contentRequired'))
    return
  }

  saving.value = true
  try {
    const response = await api.put(`/posts/${props.post.postId}`, {
      content: editContent.value
    })

    if (response.data.success) {
      const updatedPost = { ...props.post, content: editContent.value }
      isEditing.value = false
      emit('updated', updatedPost)
    }
  } catch (error) {
    console.error('Error updating post:', error)
    alert(`${t('common.updateFailed')}: ${error.response?.data?.error || error.message}`)
  } finally {
    saving.value = false
  }
}

const deletePost = async () => {
  const confirmed = await confirmDialog.value.open()
  if (!confirmed) return

  try {
    const response = await api.delete(`/posts/${props.post.postId}`)
    if (response.data.success) {
      emit('deleted', props.post.postId)
    }
  } catch (error) {
    console.error('Error deleting post:', error)
    alert(t('common.deleteFailed'))
  }
}

// Reaction functions
const toggleReaction = async (reactionIcon) => {
  const reactionType = reactionEmojis.find(e => e.icon === reactionIcon)?.name || 'like'

  try {
    if (userReaction.value === reactionIcon) {
      const response = await api.delete(`/posts/${props.post.postId}/reactions`)
      if (response.data.success) {
        userReaction.value = null
        reactions.value = reactions.value.filter(r => r.userId !== currentUser.value.userId)
      }
    } else {
      const response = await api.post(`/posts/${props.post.postId}/reactions`, {
        reactionIcon,
        reactionType
      })
      if (response.data.success) {
        userReaction.value = reactionIcon
        const existingIndex = reactions.value.findIndex(r => r.userId === currentUser.value.userId)
        if (existingIndex >= 0) {
          reactions.value[existingIndex] = { userId: currentUser.value.userId, reactionIcon, reactionType }
        } else {
          reactions.value.push({ userId: currentUser.value.userId, reactionIcon, reactionType })
        }
      }
    }
  } catch (error) {
    console.error('Error toggling reaction:', error)
  }
}

// Comment functions
const toggleComments = async () => {
  showComments.value = !showComments.value
  if (showComments.value && comments.value.length === 0) {
    await loadComments()
  }
}

const loadReactions = async () => {
  try {
    const response = await api.get(`/posts/${props.post.postId}/reactions`)
    reactions.value = response.data.reactions || []
    const userReactionObj = reactions.value.find(r => r.userId === currentUser.value?.userId)
    if (userReactionObj) {
      userReaction.value = userReactionObj.reactionIcon
    }
  } catch (error) {
    console.error('Error loading reactions:', error)
  }
}

const loadComments = async () => {
  loadingComments.value = true
  try {
    const response = await api.get(`/posts/${props.post.postId}/comments`)
    comments.value = response.data.comments || []
  } catch (error) {
    console.error('Error loading comments:', error)
  } finally {
    loadingComments.value = false
  }
}

// Load on mount
loadReactions()
loadPoll()

const postComment = async () => {
  if (!newComment.value || !newComment.value.trim()) return

  postingComment.value = true
  try {
    const response = await api.post(`/posts/${props.post.postId}/comments`, {
      content: newComment.value,
      quotedCommentId: quotedComment.value?.commentId || null
    })

    if (response.data.success) {
      comments.value.push(response.data.comment)
      emit('updated', { ...props.post, commentsCount: (props.post.commentsCount || 0) + 1 })
      newComment.value = ''
      quotedComment.value = null
    }
  } catch (error) {
    console.error('Error posting comment:', error)
    alert(t('common.commentFailed'))
  } finally {
    postingComment.value = false
  }
}

const quoteComment = (comment) => {
  quotedComment.value = comment
  newComment.value = `@${comment.username} `
}

const toggleCommentLike = async (comment) => {
  comment.likeLoading = true
  try {
    if (comment.userReaction) {
      const response = await api.delete(`/comments/${comment.commentId}/reactions`)
      if (response.data.success) {
        comment.userReaction = null
        comment.reactionCount = response.data.count || 0
      }
    } else {
      const response = await api.post(`/comments/${comment.commentId}/reactions`, {
        reactionIcon: 'mdi-heart',
        reactionType: 'love'
      })
      if (response.data.success) {
        comment.userReaction = 'mdi-heart'
        comment.reactionCount = response.data.count || 0
      }
    }
  } catch (error) {
    console.error('Error toggling comment like:', error)
  } finally {
    comment.likeLoading = false
  }
}

const getQuotedCommentText = (commentId) => {
  const comment = comments.value.find(c => c.commentId === commentId)
  return comment ? comment.content.substring(0, 100) : '[Deleted comment]'
}

const handleCommentKeydown = (event) => {
  if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
    postComment()
  }
}
</script>

<style scoped>
.post-card {
  margin-bottom: 1rem;
}

.post-content {
  white-space: pre-wrap;
  word-wrap: break-word;
}

.post-content :deep(.mention) {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}

.post-content :deep(.post-link) {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  word-break: break-all;
}

.post-content :deep(.post-link:hover) {
  opacity: 0.8;
}

.comment-text :deep(.mention) {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}

.comment-text :deep(.post-link) {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  word-break: break-all;
}

.group-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  background-color: #e8f5e9;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: normal;
  color: #4A8B7F;
  text-decoration: none;
}

.group-badge:hover {
  background-color: #c8e6c9;
}

.comment-bubble {
  background-color: #f5f5f5;
}

.quoted-comment {
  background-color: #e0e0e0;
  border-left: 3px solid #4A8B7F;
  border-radius: 0;
  font-size: 0.875rem;
  font-style: italic;
}

.comments-list {
  max-height: 400px;
  overflow-y: auto;
}

.post-content :deep(.video-embed) {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  margin: 1rem 0;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.post-content :deep(.video-embed iframe) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.comment-text :deep(.video-embed) {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  margin: 0.5rem 0;
  border-radius: 6px;
  overflow: hidden;
  background: #000;
}

.comment-text :deep(.video-embed iframe) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.reactions-count {
  cursor: pointer;
}

.reactors-card {
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}

.reactor-item {
  border-radius: 4px;
  padding: 2px 4px;
}

.reactor-item:hover {
  background: #f5f5f5;
}
</style>